import axios from "axios";
import { useEffect, useState } from "react";
import { Truck } from "lucide-react";
import { API } from "./shared";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const STATES = ["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Delhi","Goa","Gujarat","Haryana","Himachal Pradesh","Jammu and Kashmir","Jharkhand","Karnataka","Kerala","Ladakh","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Puducherry","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Chandigarh","Dadra and Nagar Haveli and Daman and Diu","Lakshadweep","Andaman and Nicobar Islands"];
const inp = { width: "100%", padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 8, marginTop: 4, fontSize: 13 };

export const emptyAddress = (user) => ({ name: user?.company || user?.name || "", phone: (user?.phone || "").replace(/\D/g, "").slice(-10), address: "", address_2: "", city: "", state: "", pincode: "" });
export const addressValid = (a) => a.name.trim().length >= 2 && a.phone.replace(/\D/g, "").length >= 10 && a.address.trim().length >= 5 && a.city.trim().length >= 2 && !!a.state && /^\d{6}$/.test(a.pincode);

export function DeliveryAddressForm({ value, onChange, lockedPincode }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const F = (k, label, props = {}) => (
    <label style={{ fontSize: 12, color: "#475569" }}>{label}
      <input data-testid={`addr-${k}`} value={value[k]} onChange={set(k)} style={inp} {...props} />
    </label>
  );
  return (
    <div data-testid="delivery-address-form" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, margin: "12px 0" }}>
      <b style={{ gridColumn: "span 2", font: "600 13px Outfit" }}><Truck size={14} style={{ verticalAlign: -2 }} /> Delivery address</b>
      {F("name", "Consignee / company name *")}
      {F("phone", "Phone *", { inputMode: "tel", maxLength: 15 })}
      <div style={{ gridColumn: "span 2" }}>{F("address", "Address line 1 *")}</div>
      <div style={{ gridColumn: "span 2" }}>{F("address_2", "Address line 2")}</div>
      {F("city", "City *")}
      <label style={{ fontSize: 12, color: "#475569" }}>State *
        <select data-testid="addr-state" value={value.state} onChange={set("state")} style={inp}>
          <option value="">Select state</option>
          {STATES.map(s => <option key={s}>{s}</option>)}
        </select>
      </label>
      {F("pincode", lockedPincode ? "Pincode * (locked to the request)" : "Pincode *", { inputMode: "numeric", maxLength: 6, readOnly: !!lockedPincode, style: lockedPincode ? { ...inp, background: "#F1F5F9", color: "#475569", cursor: "not-allowed" } : inp, title: lockedPincode ? "Printers quoted delivery for this pincode" : undefined })}
    </div>
  );
}

export function useCheckoutQuote(jobId, bidId, address) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const { pincode, state } = address;
  useEffect(() => {
    if (!/^\d{6}$/.test(pincode) || !state) { setQuote(null); return; }
    let live = true;
    setLoading(true); setErr("");
    axios.get(`${API}/jobs/${jobId}/checkout-quote`, { params: { bid_id: bidId, pincode, state } })
      .then(r => live && setQuote(r.data))
      .catch(e => live && setErr(e.response?.data?.detail || "Could not calculate delivery"))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [jobId, bidId, pincode, state]);
  return { quote, loading, err };
}

const Row = ({ label, value, testid, strong }) => (
  <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", fontSize: strong ? 15 : 13, fontWeight: strong ? 700 : 400 }}>
    <span>{label}</span><span data-testid={testid}>{value}</span>
  </div>
);

export function CheckoutBreakdown({ quote, loading, err }) {
  if (loading) return <div data-testid="checkout-loading" style={{ fontSize: 12, color: "#64748b", margin: "8px 0" }}>Calculating GST & delivery…</div>;
  if (err) return <div data-testid="checkout-err" style={{ fontSize: 12, color: "#b91c1c", margin: "8px 0" }}>{String(err)}</div>;
  if (!quote) return <div data-testid="checkout-hint" style={{ fontSize: 12, color: "#64748b", margin: "8px 0" }}>Enter the delivery state & pincode to see GST and delivery charges.</div>;
  const d = quote.delivery;
  const split = quote.gst_split?.is_intra_state ? `CGST ${quote.gst_rate_pct / 2}% + SGST ${quote.gst_rate_pct / 2}%` : `IGST ${quote.gst_rate_pct}%`;
  return (
    <div data-testid="checkout-breakdown" style={{ border: "1px solid #E2E8F0", borderRadius: 8, padding: "10px 14px", margin: "10px 0", background: "#F8FAFC" }}>
      <Row label="Base price (winning bid)" value={money(quote.base_price_inr)} testid="checkout-base" />
      <Row label={`GST ${quote.gst_rate_pct}% (${split})`} value={money(quote.gst_inr)} testid="checkout-gst" />
      {quote.gst_class_label && (
        <div data-testid="checkout-gst-class" style={{ fontSize: 11, color: "#64748b", margin: "-2px 0 6px" }}>
          {quote.gst_class_label} · {quote.hsn_sac?.hsn ? `HSN ${quote.hsn_sac.hsn}` : `SAC ${quote.hsn_sac?.sac}`}
          {quote.gst_class_changed_by_supplier && " · class confirmed by the printer"}. Rate locks when you pay.
        </div>
      )}
      <Row label={`Delivery to ${d.pincode} · ${d.label}${d.etd ? ` · ETA ${d.etd}` : ""}`} value={money(d.delivery_base_inr)} testid="checkout-delivery" />
      {d.handling_inr > 0 && <div style={{ fontSize: 11, color: "#64748b", marginTop: -3 }}>Includes courier rate {money(d.carrier_rate_inr)} + handling {money(d.handling_inr)}</div>}
      <Row label={`GST on delivery ${quote.delivery_gst_rate_pct}%`} value={money(quote.delivery_gst_inr)} testid="checkout-delivery-gst" />
      <div style={{ borderTop: "1px solid #CBD5E1", marginTop: 4 }} />
      <Row label="Total into escrow" value={money(quote.total_inr)} testid="checkout-total" strong />
    </div>
  );
}
